// Generated by GitHub Copilot
import React, { useState, useEffect } from "react";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import PrivilegeToggle from "../PrivilegeManager/PrivilegeToggle";
import FeatureToggle from "../FeatureManager/FeatureToggle";
import { getFeatureToggleDefinitions } from "../../../connections/featureDefinitions";
import ConfirmDialog from "../../common/ConfirmDialog/ConfirmDialog";
import styles from "./EditUserForm.module.css";
import { trackUserUpdate, trackPrivilegeChange, trackUserModalInteraction } from "../../../utils/modernUITracking";

/**
 * Edit User Form Modal Component
 */
const EditUserForm = ({ user, show, onClose, onUserUpdated, currentUser }) => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    privileges: {},
    features: {},
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  // const [featuresLoading, setFeaturesLoading] = useState(true);

  // Available privileges and features
  const availablePrivileges = [
    {
      name: "editor",
      displayName: "Editor",
      description: "Can edit problems and notes",
      riskLevel: "low",
    },
    {
      name: "create",
      displayName: "Create",
      description: "Can create new problems",
      riskLevel: "low",
    },
    {
      name: "search",
      displayName: "Search",
      description: "Can search across all data",
      riskLevel: "medium",
    },
    {
      name: "revert",
      displayName: "Revert",
      description: "Can revert problem versions",
      riskLevel: "medium",
    },
    {
      name: "archive",
      displayName: "Archive",
      description: "Can archive problems",
      riskLevel: "high",
      dependencies: [],
    },
    {
      name: "configuration",
      displayName: "Configuration",
      description: "Can edit delivery streams and other system configuration",
      riskLevel: "medium",
    },
    {
      name: "interested_parties",
      displayName: "View Interested Parties",
      description: "Can view interested parties information in user records",
      riskLevel: "low",
    },
    {
      name: "impact",
      displayName: "Impact",
      description:
        "Can create, edit, and delete impact records in Impact Reporting",
      riskLevel: "low",
    },
    {
      name: "impact_delete",
      displayName: "Impact Delete",
      description:
        "Can permanently delete Report Impact entries (MIM/Eternium capability)",
      riskLevel: "high",
    },
    {
      name: "incident_artifact_edit",
      displayName: "Incident Links",
      description:
        "Can attach, replace and remove the Exec Summary and PIR links on a resolved incident (Eternium capability)",
      riskLevel: "medium",
    },
    {
      name: "modern_status_page",
      displayName: "Modern Status Page",
      description: "Access to modern StatusPage features and interface",
      riskLevel: "low",
    },
    {
      name: "deity",
      displayName: "Deity",
      description: "Full system access - use with extreme caution",
      riskLevel: "high",
    },
  ];

  // Available features will be loaded from API
  const [availableFeatures, setAvailableFeatures] = useState([]);

  // Initialize form data when user prop changes
  useEffect(() => {
    if (user) {
      const initialData = {
        email: user.email || "",
        name: user.name || "",
        privileges: { ...user.privileges } || {},
        features: { ...user.features } || {},
      };
      setFormData(initialData);
      setOriginalFormData(initialData);
      setHasChanges(false);
      setValidationErrors({});
      setSubmitError(null);
    }
  }, [user]);

  // Generated by GitHub Copilot
  // Load available features from API
  useEffect(() => {
    const loadFeatures = async () => {
      try {
        // setFeaturesLoading(true);
        const featureDefinitions = await getFeatureToggleDefinitions();

        // Convert API format to the format expected by the component
        const features = featureDefinitions.map((def) => ({
          name: def.name,
          displayName: def.displayName,
          description: def.description,
          category: def.category,
          experimental: def.experimental,
          beta: def.beta,
        }));

        setAvailableFeatures(features);
      } catch (error) {
        console.error("Failed to load feature definitions:", error);
        // Set empty array on error to prevent UI issues
        setAvailableFeatures([]);
      } finally {
        // setFeaturesLoading(false);
      }
    };

    loadFeatures();
  }, []);

  /**
   * Handle form field changes
   */
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setHasChanges(true);

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  /**
   * Handle privilege changes
   */
  const handlePrivilegeChange = (privilege, enabled) => {
    setFormData((prev) => ({
      ...prev,
      privileges: {
        ...prev.privileges,
        [privilege]: enabled,
      },
    }));
    setHasChanges(true);

    // Track privilege toggle (find risk level from availablePrivileges)
    const privilegeInfo = availablePrivileges.find(p => p.name === privilege);
    trackPrivilegeChange(privilege, enabled, {
      userEmail: user.email,
      adminEmail: currentUser.email,
      riskLevel: privilegeInfo?.riskLevel || 'unknown'
    });
  };

  /**
   * Handle feature changes
   */
  const handleFeatureChange = (feature, enabled) => {
    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: enabled,
      },
    }));
    setHasChanges(true);
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters long";
    }

    // Privilege validation
    const selectedPrivileges = Object.entries(formData.privileges)
      .filter(([key, value]) => value === true)
      .map(([key]) => key);

    if (selectedPrivileges.includes("deity") && !currentUser.privileges.deity) {
      errors.privileges = "Only deity users can assign deity privileges";
    }

    // Prevent user from removing their own deity privilege if they're the only deity
    if (
      user.email === currentUser.email &&
      user.privileges?.deity &&
      !formData.privileges.deity
    ) {
      errors.privileges = "You cannot remove your own deity privileges";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    // Calculate what changed
    const privilegesAdded = [];
    const privilegesRemoved = [];
    const featuresAdded = [];
    const featuresRemoved = [];

    if (originalFormData) {
      // Compare privileges
      Object.keys(formData.privileges).forEach(key => {
        if (formData.privileges[key] && !originalFormData.privileges[key]) {
          privilegesAdded.push(key);
        } else if (!formData.privileges[key] && originalFormData.privileges[key]) {
          privilegesRemoved.push(key);
        }
      });

      // Compare features
      Object.keys(formData.features).forEach(key => {
        if (formData.features[key] && !originalFormData.features[key]) {
          featuresAdded.push(key);
        } else if (!formData.features[key] && originalFormData.features[key]) {
          featuresRemoved.push(key);
        }
      });
    }

    try {
      const response = await fetch(
        `/api/v1/config/users/${encodeURIComponent(user.email)}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            privileges: formData.privileges,
            features: formData.features,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to update user: ${response.status} ${response.statusText}`
        );
      }

      const updatedUser = await response.json();

      // Track successful update
      trackUserUpdate(user.email, {
        adminEmail: currentUser.email,
        success: true,
        privilegesAdded,
        privilegesRemoved,
        featuresAdded,
        featuresRemoved,
        nameChanged: formData.name !== originalFormData?.name
      });

      onUserUpdated(updatedUser);
      onClose();
    } catch (error) {
      console.error("Error updating user:", error);

      // Track failed update
      trackUserUpdate(user.email, {
        adminEmail: currentUser.email,
        success: false,
        error: error.message
      });

      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle modal close with unsaved changes warning
   */
  const handleClose = () => {
    if (hasChanges) {
      trackUserModalInteraction('edit', 'close', {
        userEmail: user.email,
        adminEmail: currentUser.email,
        hasUnsavedChanges: true
      });
      setShowConfirmDialog(true);
    } else {
      trackUserModalInteraction('edit', 'close', {
        userEmail: user.email,
        adminEmail: currentUser.email,
        hasUnsavedChanges: false
      });
      onClose();
    }
  };

  /**
   * Handle confirm dialog close - user confirmed discard
   */
  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  /**
   * Handle confirm dialog cancel - user wants to keep editing
   */
  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  /**
   * Check if user can be edited
   */
  const canEdit = () => {
    // Deity users can edit anyone except themselves (for safety on critical privileges)
    if (currentUser.privileges.deity && user.email !== currentUser.email) {
      return true;
    }

    // Deity users can edit themselves but with restrictions
    if (currentUser.privileges.deity && user.email === currentUser.email) {
      return true;
    }

    // Editor users can edit non-deity users
    if (currentUser.privileges.editor && !user.privileges?.deity) {
      return true;
    }

    return false;
  };

  if (!user) return null;

  return (
    <>
      {show && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaEdit className={styles.modalIcon} />
                <span>Edit User: {user.email}</span>
              </h2>
              <button
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close"
                type="button"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.modalForm}>
              <div className={styles.modalBody}>
                {!canEdit() && (
                  <div className={`${styles.alert} ${styles.alertWarning}`}>
                    You do not have permission to edit this user.
                  </div>
                )}

                {submitError && (
                  <div className={`${styles.alert} ${styles.alertDanger}`}>
                    {submitError}
                  </div>
                )}

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Email Address</label>
                    <input
                      type="email"
                      className={styles.formInput}
                      value={formData.email}
                      disabled
                    />
                    <span className={styles.formHelperText}>
                      Email addresses cannot be changed after creation.
                    </span>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      className={`${styles.formInput} ${
                        validationErrors.name ? styles.invalid : ""
                      }`}
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={!canEdit() || isSubmitting}
                      required
                    />
                    {validationErrors.name && (
                      <span className={styles.formError}>
                        {validationErrors.name}
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.cardContainer}>
                  <div className={styles.card}>
                    <div className={styles.cardHeader}>
                      <h6 className={styles.cardTitle}>Privileges</h6>
                      {validationErrors.privileges && (
                        <small className={styles.cardError}>
                          {validationErrors.privileges}
                        </small>
                      )}
                    </div>
                    <div className={styles.cardBody}>
                      {availablePrivileges.map((privilege) => (
                        <PrivilegeToggle
                          key={privilege.name}
                          privilege={privilege}
                          enabled={formData.privileges[privilege.name] || false}
                          onChange={handlePrivilegeChange}
                          disabled={
                            !canEdit() ||
                            isSubmitting ||
                            (privilege.name === "deity" &&
                              !currentUser.privileges.deity) ||
                            (user.email === currentUser.email &&
                              privilege.name === "deity" &&
                              formData.privileges[privilege.name]) // Can't remove own deity
                          }
                        />
                      ))}
                    </div>
                  </div>

                  <div className={styles.card}>
                    <div className={styles.cardHeader}>
                      <h6 className={styles.cardTitle}>Features</h6>
                    </div>
                    <div className={styles.cardBody}>
                      {availableFeatures.map((feature) => (
                        <FeatureToggle
                          key={feature.name}
                          feature={feature}
                          enabled={formData.features[feature.name] || false}
                          onChange={handleFeatureChange}
                          disabled={!canEdit() || isSubmitting}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {user.email === currentUser.email && (
                  <div className={`${styles.alert} ${styles.alertInfo}`}>
                    <strong>Note:</strong> You are editing your own user
                    account. Some restrictions apply to prevent accidental
                    privilege removal.
                  </div>
                )}
              </div>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonSecondary}`}
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  <FaTimes className={styles.buttonIcon} />
                  <span>Cancel</span>
                </button>

                <button
                  type="submit"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={!canEdit() || !hasChanges || isSubmitting}
                >
                  <FaSave className={styles.buttonIcon} />
                  <span>{isSubmitting ? "Saving..." : "Save Changes"}</span>
                </button>
              </div>
            </form>

            <ConfirmDialog
              show={showConfirmDialog}
              title="Discard Changes?"
              message="You have unsaved changes. Are you sure you want to discard them?"
              confirmLabel="Discard Changes"
              cancelLabel="Keep Editing"
              onConfirm={handleConfirmClose}
              onCancel={handleCancelClose}
              variant="danger"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default EditUserForm;
